import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { LEGAL_LINKS, OPERATOR } from "@/lib/legal";
import { PLAY_PACKAGE } from "@/lib/invite";
import { tw } from "@/lib/theme";

const PLAY_URL = `https://play.google.com/store/apps/details?id=${PLAY_PACKAGE}`;

const FEATURES = [
  {
    title: "Parallel",
    body: "Capture the same moment with friends — countdown, lock, and reveal together.",
  },
  {
    title: "Stories & Frames",
    body: "Camera-only live moments. No gallery uploads. Real photos from real life.",
  },
  {
    title: "Drifts",
    body: "Short vertical video from your world — swipe, vibe, and share with people you know.",
  },
  {
    title: "Built for friends",
    body: "Contact sync, audience controls, and chat — your circle, not an algorithm.",
  },
] as const;

export function LandingPage() {
  useEffect(() => {
    document.title = `${OPERATOR.product} — Capture the same moment together`;
    const description =
      "Daywia is an Android app for friends to share live photos, Drifts, and Parallel captures. Download on Google Play.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description);
  }, []);

  return (
    <div className={`min-h-dvh ${tw.canvas}`}>
      <header className="border-b border-border bg-surface/90 backdrop-blur dark:border-dark-border dark:bg-dark-surface/90">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <span className="text-lg font-bold text-primary dark:text-primary-dark">
            {OPERATOR.product}
          </span>
          <nav className="hidden items-center gap-5 text-sm min-[640px]:flex">
            <Link to="/about" className={tw.link}>
              About
            </Link>
            <Link to="/faq" className={tw.link}>
              FAQ
            </Link>
            <Link to="/download" className={tw.link}>
              Download
            </Link>
          </nav>
          <a href={PLAY_URL} target="_blank" rel="noopener noreferrer">
            <Button>Get the app</Button>
          </a>
        </div>
      </header>

      <main>
        <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-24">
          <div className="max-w-2xl space-y-6">
            <p className="text-sm font-semibold uppercase tracking-widest text-primary dark:text-primary-dark">
              Android app · India
            </p>
            <h1 className={`text-4xl font-bold tracking-tight sm:text-5xl ${tw.heading}`}>
              A friendship network built around capturing the same moment together.
            </h1>
            <p className={`text-lg leading-relaxed ${tw.muted}`}>
              {OPERATOR.product} is mobile-first: live camera captures, Parallel sessions with
              mutual friends, and a feed that stays close to people you actually know.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <a href={PLAY_URL} target="_blank" rel="noopener noreferrer">
                <Button>Download on Google Play</Button>
              </a>
              <Link to="/download">
                <Button variant="outline">Learn more</Button>
              </Link>
            </div>
            <p className={`text-sm ${tw.muted}`}>
              This website is for downloads, invites, and legal info. Sign in and post from the
              Android app.
            </p>
          </div>
        </section>

        <section className="border-t border-border bg-soft/60 dark:border-dark-border dark:bg-dark-section/40">
          <div className="mx-auto grid max-w-5xl gap-6 px-4 py-14 sm:grid-cols-2 sm:px-6">
            {FEATURES.map((feature) => (
              <div key={feature.title} className={`${tw.card} p-6`}>
                <h2 className={`text-lg font-semibold ${tw.heading}`}>{feature.title}</h2>
                <p className={`mt-2 text-sm leading-relaxed ${tw.muted}`}>{feature.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
          <div className={`${tw.card} space-y-4 p-8 text-center`}>
            <h2 className={`text-2xl font-bold ${tw.heading}`}>Ready to join?</h2>
            <p className={`mx-auto max-w-md text-sm ${tw.muted}`}>
              Install {OPERATOR.product} on Android. Got an invite link? Open it on your phone —
              we&apos;ll route you to the app or Play Store.
            </p>
            <div className="flex flex-wrap justify-center gap-3 pt-2">
              <a href={PLAY_URL} target="_blank" rel="noopener noreferrer">
                <Button>Get Daywia on Android</Button>
              </a>
              <Link to="/legal">
                <Button variant="ghost">Legal & policies</Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border dark:border-dark-border">
        <div className="mx-auto max-w-5xl space-y-6 px-4 py-10 sm:px-6">
          <nav className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm">
            {LEGAL_LINKS.map((link) => (
              <Link key={link.to} to={link.to} className={tw.link}>
                {link.label}
              </Link>
            ))}
          </nav>
          <p className={`text-center text-xs ${tw.muted}`}>
            © {new Date().getFullYear()} {OPERATOR.product} · Operated by {OPERATOR.name}
          </p>
        </div>
      </footer>
    </div>
  );
}
